import { OFFICE_AUTH_DIALOG_PATH } from '../../../auth/authConfig';

export type OfficeAuthDialogResult =
    | { status: 'success'; accessToken: string }
    | { status: 'error'; code?: string | number; message?: string };

function getDialogUrl(): string {
    const origin =
        typeof window !== 'undefined' ? window.location.origin.replace(/\/+$/, '') : '';
    return `${origin}${OFFICE_AUTH_DIALOG_PATH}`;
}

export function openOfficeAuthDialog(): Promise<string> {
    return new Promise((resolve, reject) => {
        const officeUi = (Office as any)?.context?.ui;
        if (!officeUi || typeof officeUi.displayDialogAsync !== 'function') {
            reject(new Error('Office dialog API is not available'));
            return;
        }

        officeUi.displayDialogAsync(
            getDialogUrl(),
            { height: 60, width: 30, displayInIframe: false },
            (asyncResult: any) => {
                if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
                    reject(
                        new Error(
                            asyncResult.error?.message ||
                                'Failed to open Microsoft sign-in dialog'
                        )
                    );
                    return;
                }

                const dialog = asyncResult.value;

                const cleanup = () => {
                    try {
                        dialog.close();
                    } catch {
                        // dialog may already be closed
                    }
                };

                dialog.addEventHandler(
                    (Office as any).EventType.DialogMessageReceived,
                    (arg: { message?: string; error?: number }) => {
                        if (arg.error) {
                            cleanup();
                            reject(new Error(`Dialog error: ${arg.error}`));
                            return;
                        }

                        try {
                            const msg = JSON.parse(arg.message || '{}') as OfficeAuthDialogResult;
                            cleanup();
                            if (msg.status === 'success' && msg.accessToken) {
                                resolve(msg.accessToken);
                                return;
                            }
                            const errorMessage =
                                msg.status === 'error'
                                    ? msg.message || 'Microsoft sign-in did not return an access token'
                                    : 'Microsoft sign-in did not return an access token';
                            reject(new Error(errorMessage));
                        } catch (parseError) {
                            cleanup();
                            reject(parseError);
                        }
                    }
                );

                dialog.addEventHandler(
                    (Office as any).EventType.DialogEventReceived,
                    (arg: { error?: number; message?: string }) => {
                        cleanup();
                        reject(
                            new Error(
                                arg.message ||
                                    `Sign-in dialog closed (error ${arg.error ?? 'unknown'})`
                            )
                        );
                    }
                );
            }
        );
    });
}

/** Human-readable hints for Office.auth.getAccessToken error codes. */
export function describeOfficeAuthErrorCode(code: number | string | undefined): string {
    switch (Number(code)) {
        case 13001:
            return 'Microsoft sign-in required. Please try again.';
        case 13002:
            return 'User cancelled Microsoft sign-in.';
        case 13003:
            return 'Microsoft sign-in is not supported in this Outlook context.';
        case 13004:
            return 'Microsoft sign-in API is not available.';
        case 13005:
            return 'Microsoft sign-in failed due to an invalid resource.';
        case 13006:
            return 'Microsoft sign-in failed due to an invalid grant.';
        case 13007:
            return 'Admin consent may be required for this Microsoft app.';
        case 13008:
            return 'Microsoft sign-in was interrupted.';
        case 13009:
            return 'Microsoft identity platform error during sign-in.';
        case 13010:
            return 'Microsoft sign-in requires a supported account type.';
        case 13012:
            return 'Office SSO configuration mismatch — check Azure app ffbf34bc and manifest v23.';
        default:
            return 'Microsoft sign-in failed. Please try again.';
    }
}
